import { CheckCircleIcon, CreditCardIcon, LockClosedIcon } from '@heroicons/react/24/outline';
import mastercardVisaIcon from '@workspace/core-ts/assets/mastercard-visa-blanc.svg';
import { FieldRadioGroup, Question, SmartButton, SmartFormEmbed } from '@workspace/core-ts/components';
import { normalizeEuro } from '@workspace/utils';
import DOMPurify from 'dompurify';
import { useCallback, useEffect, useState } from 'react';
import { useMutationPay, useMutationPayEmbedded } from '../../api';
import { paiementStepContent } from '../../content';
import { SmartFormGlobal, useGlobalForm, useSmartFormGlobal } from '../../contexts';
import { useAvenant, useContent, useSource } from '../../hooks';
import type { StepContentConfig } from '../../utils';
import type { PayEmbeddedResponse } from '@workspace/api';

interface PaiementStepProps {
    content?: StepContentConfig;
    showConseillerOptions?: boolean;
    firstPaymentLabel?: string | ((tarifs: { formuleChoisie: string; fractionnement: string; taxeAttentat: number; premierPaiement: number }) => string);
    customTarifs?: { premierPaiement: number; formuleChoisie: string; fractionnement: string; taxeAttentat: number } | null;
    /** Active le SmartForm SogeCommerce inline (pas de redirection vers la page de
     *  paiement hébergée). Quand le mode embedded est activé, le formulaire affiche
     *  les champs CB directement dans la page après soumission. */
    embedded?: boolean;
}

export function PaiementStep({ content = paiementStepContent, firstPaymentLabel, customTarifs, embedded = false }: PaiementStepProps) {
    const methods = useSmartFormGlobal<any>();
    const { meta, actions } = useGlobalForm();
    const mutationPay = useMutationPay(meta.token);
    const mutationPayEmbedded = useMutationPayEmbedded(meta.token);
    const r = useContent();

    const { isConseiller, isCourtier } = useSource();
    const isAvenant = useAvenant();

    const [embeddedData, setEmbeddedData] = useState<PayEmbeddedResponse | null>(null);
    const [embeddedError, setEmbeddedError] = useState<string | null>(null);

    // Auto-embed = client direct ou avenant, quand embedded=true.
    // Les conseillers/courtiers (hors avenant) doivent d'abord choisir
    // "envoi de lien" via le radio, donc on garde le résumé intermédiaire.
    const shouldAutoEmbed = embedded && !((isConseiller || isCourtier) && !isAvenant);

    useEffect(() => {
        if ((isConseiller || isCourtier) && !isAvenant) {
            methods.setValue('DemandeContrat.ModePaiementComptant', 'M');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!shouldAutoEmbed || embeddedData || mutationPayEmbedded.isPending) return;

        (async () => {
            const formValues = methods.getValues();
            try {
                const result = await mutationPayEmbedded.mutateAsync({
                    ...formValues,
                    url_return: window.location.origin.toString(),
                } as any);
                setEmbeddedData(result);
            } catch {
                // Fallback redirect si l'embedded échoue au démarrage
                try {
                    const response = await mutationPay.mutateAsync({
                        ...formValues,
                        url_return: window.location.origin.toString(),
                        redirection: false,
                    });
                    if (response) window.location.href = response as unknown as string;
                } catch {
                    setEmbeddedError('Impossible d\'initialiser le paiement. Merci de réessayer.');
                }
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSubmit = async (data: any) => {
        const modePaiement = methods.watch('DemandeContrat.ModePaiementComptant');

        // Mode conseiller/courtier (envoi de lien) : toujours redirect classique.
        if ((isConseiller || isCourtier) && modePaiement === 'M') {
            await mutationPay.mutateAsync({
                ...data,
                url_return: window.location.origin.toString(),
                redirection: false,
            });
            return actions.goNextQuestion();
        }

        // Mode embedded SogeCommerce : récupère le formToken puis affiche le SmartForm inline.
        if (embedded) {
            try {
                const result = await mutationPayEmbedded.mutateAsync({
                    ...data,
                    url_return: window.location.origin.toString(),
                } as any);
                setEmbeddedData(result);
            } catch {
                // Fallback redirect si l'embedded échoue
                const response = await mutationPay.mutateAsync({
                    ...data,
                    url_return: window.location.origin.toString(),
                    redirection: false,
                });
                if (response) window.location.href = response as unknown as string;
            }
            return;
        }

        // Mode redirect classique (Sogenactif / SogeCommerce hébergé).
        const response = await mutationPay.mutateAsync({
            ...data,
            url_return: window.location.origin.toString(),
            redirection: false,
        });

        if (response) window.location.href = response as unknown as string;
    };

    const handlePaid = useCallback(() => {
        actions.goNextQuestion();
    }, [actions]);

    const handleSmartFormError = useCallback((message: string) => {
        setEmbeddedError(message);
    }, []);

    const getTarifs = () => {
        const Devis = methods.watch('Devis');
        const formuleChoisie = methods.watch('DemandeTarif.Police.FormuleChoisie');
        const fractionnement = methods.watch('DemandeTarif.Police.FractionnementChoisi') || 'M';

        if (!Devis?.Formule?.MontantComptantPromo) return null;

        return {
            premierPaiement: Devis.Formule.MontantComptantPromo,
            fractionnement,
            formuleChoisie,
            taxeAttentat: Devis.Formule.MontantTaxeAttentat || 0,
        };
    };

    const tarifs = customTarifs !== undefined ? customTarifs : getTarifs();

    const isPending = mutationPay.isPending || mutationPayEmbedded.isPending;

    // État de chargement (auto-embed en attente OU soumission conseiller en cours)
    if ((isPending || shouldAutoEmbed) && !embeddedData && !embeddedError) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px]">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mb-4"></div>
                <p className="text-slate-500">{r(content.redirectionText)}</p>
            </div>
        );
    }

    // Erreur d'init en mode auto-embed (fallback redirect aussi échoué)
    if (shouldAutoEmbed && embeddedError && !embeddedData) {
        return (
            <div className="max-w-md mx-auto mt-10 px-4">
                <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                    {embeddedError}
                </div>
            </div>
        );
    }

    // SmartForm affiché après récupération du formToken
    if (embeddedData) {
        return (
            <div className="max-w-2xl mx-auto mt-6 sm:mt-10 mb-20 px-4">
                <Question.Header>
                    <CreditCardIcon className="w-12 h-12 mx-auto mb-4 stroke-1" />
                    <Question.Title>{r(content.mainTitle)}</Question.Title>
                </Question.Header>

                <div className="w-full max-w-md mx-auto mt-6">
                    {/* Carte de paiement unifiée : montant + formulaire CB + sécurité */}
                    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                        {/* Header montant */}
                        <div className="px-6 pt-6 pb-5 border-b border-slate-100 flex flex-col items-center">
                            <p className="text-xs uppercase tracking-wider text-slate-500 mb-1">Montant à régler</p>
                            <p className="text-4xl font-bold text-primary tabular-nums tracking-tight">
                                {tarifs ? normalizeEuro(tarifs.premierPaiement) : '...'}
                            </p>
                        </div>

                        {/* SmartForm */}
                        <div className="px-6 py-6">
                            {/* Krypton affiche ses propres erreurs de validation
                                (CLIENT_xxx) en interne — pas besoin de doublon
                                React. On garde onError pour le tracking
                                analytics au cas où. */}
                            <SmartFormEmbed
                                formToken={embeddedData.formToken}
                                publicKey={embeddedData.publicKey}
                                endpoint={embeddedData.endpoint}
                                onPaid={handlePaid}
                                onError={handleSmartFormError}
                            />
                        </div>

                        {/* Footer sécurité */}
                        <div className="px-6 py-3 bg-slate-50 border-t border-slate-100 flex items-center gap-2 text-xs text-slate-600">
                            <LockClosedIcon className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                            <span>{r(content.paymentSecureDescription)}</span>
                            <img
                                src={mastercardVisaIcon}
                                alt="Visa Mastercard"
                                className="h-3 ml-auto opacity-70"
                            />
                        </div>
                    </div>

                    {/* Note assurance en dessous de la carte */}
                    <div className="mt-4 flex items-start gap-2 text-xs text-slate-500 px-1">
                        <CheckCircleIcon className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                        <p
                            className="whitespace-pre-line"
                            dangerouslySetInnerHTML={{
                                __html: DOMPurify.sanitize((() => {
                                    const note = r(content.assuranceNote);
                                    if (typeof note === 'string') {
                                        return note.replace(/\{\{([^}]+)\}\}/g, (match: string, path: string) => {
                                            const value = methods.getValues(path.trim());
                                            return value || match;
                                        });
                                    }
                                    return String(note);
                                })()),
                            }}
                        />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <SmartFormGlobal
            {...methods}
            className="max-w-2xl mx-auto mt-6 sm:mt-10 mb-20"
            onSubmit={handleSubmit}
        >
            <Question.Header>
                <CreditCardIcon className="w-12 h-12 mx-auto mb-4 stroke-1" />
                <Question.Title>{r(content.mainTitle)}</Question.Title>
            </Question.Header>

            <div className="w-full max-w-md space-y-4 mb-8">
                {isConseiller && !isAvenant && (
                    <FieldRadioGroup
                        label="Souhaitez-vous utiliser l'envoi du lien de paiement pour valider le contrat ?"
                        name="DemandeContrat.ModePaiementComptant"
                        options={[
                            { label: 'Oui', value: 'M' },
                            { label: 'Non', value: null },
                        ]}
                        required={false}
                        type="small"
                    />
                )}

                <div className="rounded-xl p-6 text-center">
                    <p className="text-sm text-gray-600 mb-2">Montant à régler</p>
                    <p className="text-3xl font-bold text-primary">{tarifs ? normalizeEuro(tarifs.premierPaiement) : '...'}</p>
                    {tarifs?.fractionnement === 'M' && (
                        <p className="text-xs text-gray-500 mt-2">
                            {firstPaymentLabel
                                ? typeof firstPaymentLabel === 'function'
                                    ? <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(firstPaymentLabel(tarifs)) }} />
                                    : firstPaymentLabel
                                : (() => {
                                    const Tarif = methods.watch('Tarif');
                                    const fraisDossier = Tarif?.MontantFraisDossier || 0;
                                    return fraisDossier > 0
                                        ? 'Première mensualité (+ frais de dossier) à régler par carte bancaire.'
                                        : 'Première mensualité à régler par carte bancaire.';
                                })()}
                        </p>
                    )}
                </div>

                <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                    <div className="flex items-center gap-3 mb-2">
                        <LockClosedIcon className="w-5 h-5 text-emerald-500" />
                        <h3 className="font-medium text-sm">{r(content.paymentSecureTitle)}</h3>
                        <img
                            src={mastercardVisaIcon}
                            alt="Visa Mastercard"
                            className="h-4 ml-auto"
                        />
                    </div>
                    <p className="text-xs text-gray-600">{r(content.paymentSecureDescription)}</p>
                </div>

                <div className="flex items-start gap-2 text-xs text-slate-600">
                    <CheckCircleIcon className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <p
                        className="whitespace-pre-line"
                        dangerouslySetInnerHTML={{
                            __html: DOMPurify.sanitize((() => {
                                const note = r(content.assuranceNote);
                                if (typeof note === 'string') {
                                    return note.replace(/\{\{([^}]+)\}\}/g, (match: string, path: string) => {
                                        const value = methods.getValues(path.trim());
                                        return value || match;
                                    });
                                }
                                return String(note);
                            })()),
                        }}
                    />

                </div>
            </div>

            <SmartButton loading={isPending}>{r(content.buttonText)}</SmartButton>
        </SmartFormGlobal>
    );
}
